import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Bell, Info, ArrowRight } from "lucide-react";

/** Shows after user logs in if they have notifications */
export default function LoginNotifications({ trigger }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        if (!trigger) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/auth/notifications");
                if (cancelled) return;
                if (r.data.items?.length > 0) {
                    setItems(r.data.items);
                    setOpen(true);
                }
            } catch (_) {}
        })();
        return () => { cancelled = true; };
    }, [trigger]);

    const go = (to) => {
        setOpen(false);
        navigate(to);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-md rounded-md border-slate-200" data-testid="login-notifications-dialog" aria-describedby={undefined}>
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <Bell className="h-5 w-5 text-brand" strokeWidth={1.75} />
                        <DialogTitle className="font-heading text-xl tracking-tight">Welcome back</DialogTitle>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">A quick heads-up about your account.</p>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    {items.map((it, i) => (
                        <div key={`${it.title}-${i}`} className={`p-4 border rounded-md ${
                            it.level === "warning"
                                ? "bg-amber-50 border-amber-200"
                                : "bg-slate-50 border-slate-200"
                        }`} data-testid={`notif-${i}`}>
                            <div className="flex items-start gap-3">
                                {it.level === "warning"
                                    ? <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.75} />
                                    : <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" strokeWidth={1.75} />}
                                <div className="flex-1">
                                    <p className="font-heading font-semibold text-slate-900 text-sm">{it.title}</p>
                                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{it.body}</p>
                                    {it.cta && (
                                        <button
                                            onClick={() => go(it.cta.to)}
                                            data-testid={`notif-cta-${i}`}
                                            className="mt-2.5 inline-flex items-center gap-1 text-xs uppercase tracking-[0.16em] font-mono text-brand hover:underline underline-offset-4"
                                        >
                                            {it.cta.label} <ArrowRight className="h-3 w-3" strokeWidth={2} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="notif-dismiss-btn" className="rounded-md">
                        Dismiss
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
